import { HttpClient } from "@/api/services/HttpClient";

/**
 * Common utility helpers used throughout the application
 */

// Base64 encoding for email addresses (simple obfuscation)
export const encryptBase64 = (text: string): string => {
  return window.btoa(unescape(encodeURIComponent(text)));
};

// Base64 decoding
export const decryptBase64 = (encoded: string): string => {
  try {
    return decodeURIComponent(escape(window.atob(encoded)));
  } catch (e) {
    console.error("Error decoding base64:", e);
    return "";
  }
};

// // Set/get cookie functions that use localStorage
// export const setCookie = (name: string, value: string, exdays: number = 30): void => {
//   try {
//     localStorage.setItem(name, value);
//   } catch (error) {
//     console.error("Error setting localStorage:", error);
//   }
// };

// export const getCookie = (name: string): string => {
//   try {
//     const localValue = localStorage.getItem(name);
//     if (localValue) {
//       return localValue;
//     }
//   } catch (error) {
//     console.error("Error accessing localStorage:", error);
//   }
//   return "";
// };

// Set workspace ID in localStorage
// export const setWorkspaceId = (workspaceId: string): void => {
//   if (!workspaceId) return;

//   // Set in localStorage
//   localStorage.setItem("workspaceId", workspaceId);
//   console.log(`Workspace ID set to: ${workspaceId}`);
// };

// // Set token function
// export const handleSetToken = (token: string): boolean => {
//   if (!token) {
//     console.error("Cannot set empty token");
//     return false;
//   }

//   try {
//     console.log("Setting auth token in localStorage");
//     localStorage.setItem("token", token);
//     return true;
//   } catch (error) {
//     console.error("Error setting token:", error);
//     return false;
//   }
// };

// Format date to a readable format
export const formatDate = (date: Date | string | number): string => {
  const d = new Date(date);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
};

// Check if running in development mode
export const isDevelopment = (): boolean => {
  return import.meta.env.MODE === 'development';
};

// helpers - from feat branches 

// 🟢 Get Cookie
export const getCookie = (cname: string): string => {
  return document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${cname}=`))
    ?.split("=")[1]
    ?.trim() || "";
};

// 🟢 Set Cookie
export const setCookie = (cname: string, cvalue: string, exdays: number = 10): void => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${cname}=${cvalue};${expires};path=/`;
};

// 🟢 Delete Cookie
export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

// 🟢 Logout User
export const handleLogout = (): void => {
  deleteCookie("customerToken");
  // local storage
  localStorage.removeItem("userId");
  localStorage.removeItem("role");
  localStorage.removeItem("userName");
  window.location.href = "/";
};

// 🟢 Set Auth Token
export const handleSetToken = (token: string): boolean => {
  setCookie("customerToken", token);
  HttpClient.setAxiosDefaultConfig();
  return true;
};

// 🟢 Workspace ID Management
export const setWorkspaceId = (id: string): void => {
  setCookie("workspaceId", id);
};

export const getWorkspaceId = (): string => {
  // if not in cookie, get from local storage
  if (!getCookie("workspaceId")) {
    return localStorage.getItem("workspaceId") || "";
  }
  return getCookie("workspaceId");
};

export const getClientIdFromCookie = (): string | undefined => {
  return getCookie('clientId');
};

export const setClientId = (id: string): void => {
  setCookie('clientId', id)
}

// 🟢 Debounce Function with Immediate Execution
export const debounceWithImmediate = <T>(
  func: (props: T) => void,
  immediateFunc: (props: T) => void,
  delay: number
): ((props: T) => void) => {
  let timeoutId: NodeJS.Timeout;
  let immediateCall = true;
  return (props: T) => {
    clearTimeout(timeoutId);
    if (immediateCall) {
      immediateFunc(props);
      immediateCall = false;
    }
    timeoutId = setTimeout(() => {
      immediateCall = true;
      func(props);
    }, delay);
  };
};

// 🟢 Convert Array to Query String
interface QueryObject {
  type: string;
  data: string | number;
}

export const arrayToQueryString = (objArray: QueryObject[]): string => {
  return objArray
    .map((obj) => `${encodeURIComponent(obj.type)}=${encodeURIComponent(obj.data)}`)
    .join("&");
};

// 🟢 Email Validator
export const isEmail = (email: string): boolean => {
  const regex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  return regex.test(email);
};

// 🟢 URL Validator
export const isValidHttpUrl = (str: string): boolean => {
  try {
    new URL(str);
    return true;
  } catch {
    return false;
  }
};

// 🟢 Extract Name from URL
export const getNameFromUrl = (url: string): string => {
  const regex = /[http,https]:\/\/(.+?)\./;
  const match = url.match(regex);
  return match ? match[1] : "";
};

// 🟢 Throttle Function
export const throttle = <T extends (...args: any[]) => void>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let lastCall = 0;
  return function (...args: Parameters<T>) {
    const now = Date.now();
    if (now - lastCall >= delay) {
      func(...args);
      lastCall = now;
    }
  };
};

// 🟢 Base64 Encode & Decode
// export const encryptBase64 = (text: string): string => btoa(text);
// export const decryptBase64 = (encryptText: string): string => {
//   try {
//     return atob(encryptText);
//   } catch {
//     return "";
//   }
// };

// 🟢 Convert Text to Delta
export const convertTextToDelta = (text: string): { insert: string }[] => {
  return [{ insert: text }];
};

// 🟢 Convert Delta to Plain Text
interface DeltaOperation {
  insert: string | { mention?: { value: string } };
}

export const convertDeltaToPlainText = (ops: DeltaOperation[]): string => {
  return ops.map((op) => (typeof op.insert === "string" ? op.insert : op.insert.mention?.value || "")).join("");
};

// 🟢 Convert Delta to Text
export const convertDeltaToText = (delta?: { ops: DeltaOperation[] }): string => {
  if (!delta) return "";
  return convertDeltaToPlainText(delta.ops);
};

// 🟢 Replace `mustache` Templating with Native JavaScript
export const variabledText = (plainText: string, data: Record<string, any>): string => {
  return plainText.replace(/\{\{(.*?)\}\}/g, (_, key) => {
    const keys = key.trim().split(".");
    let value: any = data;
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) return "";
    }
    return String(value);
  });
};

// 🟢 Role Checks
export const isOrganizationAdmin = (): boolean => localStorage.getItem("role") === "ORGANIZATION_ADMIN";
export const isWorkspaceAdmin = (): boolean => localStorage.getItem("role") === "WORKSPACE_ADMIN";
export const isWorkspaceAgent = (): boolean => localStorage.getItem("role") === "WORKSPACE_AGENT";

// 🟢 Get User ID
export const getUserId = (): string | null => localStorage.getItem("userId");

// get user name
export const getUserName = (): string | null => localStorage.getItem("userName");

// Get User Email
export const getUserEmail = (): string | null => localStorage.getItem("userEmail");

// Get User Role
export const getUserRole = (): string | null => localStorage.getItem("userRole");

// Check if user data is available (for API calls)
export const isUserDataAvailable = (): boolean => {
  const userId = getUserId();
  const workspaceId = getWorkspaceId();
  return !!(userId && workspaceId);
};

// 🔥 NEW: Get user context for API calls
export const getUserContext = (): {
  userId: string | null;
  workspaceId: string | null;
  userRole: string | null;
  userEmail: string | null;
  userName: string | null;
} => {
  return {
    userId: getUserId(),
    workspaceId: getWorkspaceId(),
    userRole: getUserRole(),
    userEmail: getUserEmail(),
    userName: getUserName()
  };
};

// 🔥 NEW: Debug helper to log current user context
export const logUserContext = (): void => {
  const context = getUserContext();
  console.group("👤 Current User Context");
  console.log("User ID:", context.userId);
  console.log("Workspace ID:", context.workspaceId);
  console.log("Role:", context.userRole);
  console.log("Email:", context.userEmail);
  console.log("Name:", context.userName);
  console.log("Data Available:", isUserDataAvailable());
  console.groupEnd();
};

